export type Unit = 'ppm' | '%' | 'g/t' | 'ppb' | 'mg/l';

export interface MethodElementLimit {
  element: string;
  limit: number;
  unit: Unit;
  limitInPercent?: number;
}

export type MethodCode = 'D4A-ICP- PRE.MO/ANA/038' | 'FP-ICP PRE.MO/ANA/007' | 'BGM-MULTI-LAB' | string;

// ICP 4 acides limits (from provided table)
const ICP4A: Record<string, MethodElementLimit> = {
  // Majors as %
  Al: { element: 'Al', limit: 0.01, unit: '%' },
  Ca: { element: 'Ca', limit: 0.01, unit: '%' },
  Fe: { element: 'Fe', limit: 0.01, unit: '%' },
  K: { element: 'K', limit: 0.01, unit: '%' },
  Mg: { element: 'Mg', limit: 0.01, unit: '%' },
  Mn: { element: 'Mn', limit: 0.01, unit: '%' },
  Na: { element: 'Na', limit: 0.01, unit: '%' },
  P: { element: 'P', limit: 0.01, unit: '%' },
  S: { element: 'S', limit: 0.01, unit: '%' },
  Si: { element: 'Si', limit: 0.01, unit: '%' },
  Ti: { element: 'Ti', limit: 0.01, unit: '%' },
  // Traces ppm
  Ag: { element: 'Ag', limit: 1, unit: 'ppm' },
  As: { element: 'As', limit: 8, unit: 'ppm' },
  B: { element: 'B', limit: 5, unit: 'ppm' },
  Ba: { element: 'Ba', limit: 2, unit: 'ppm' },
  Be: { element: 'Be', limit: 1, unit: 'ppm' },
  Bi: { element: 'Bi', limit: 10, unit: 'ppm' },
  Cd: { element: 'Cd', limit: 1, unit: 'ppm' },
  Co: { element: 'Co', limit: 5, unit: 'ppm' },
  Cr: { element: 'Cr', limit: 5, unit: 'ppm' },
  Cu: { element: 'Cu', limit: 2, unit: 'ppm', limitInPercent: 0.01 },
  Ga: { element: 'Ga', limit: 5, unit: 'ppm' },
  Ge: { element: 'Ge', limit: 10, unit: 'ppm' },
  In: { element: 'In', limit: 5, unit: 'ppm' },
  Li: { element: 'Li', limit: 2, unit: 'ppm' },
  Mo: { element: 'Mo', limit: 1, unit: 'ppm' },
  Nb: { element: 'Nb', limit: 1, unit: 'ppm' },
  Ni: { element: 'Ni', limit: 2, unit: 'ppm' },
  Pb: { element: 'Pb', limit: 2, unit: 'ppm', limitInPercent: 0.01 },
  Sb: { element: 'Sb', limit: 1, unit: 'ppm' },
  Se: { element: 'Se', limit: 2, unit: 'ppm' },
  Sn: { element: 'Sn', limit: 2, unit: 'ppm' },
  Sr: { element: 'Sr', limit: 2, unit: 'ppm' },
  Ta: { element: 'Ta', limit: 1, unit: 'ppm' },
  Th: { element: 'Th', limit: 5, unit: 'ppm' },
  Tl: { element: 'Tl', limit: 1, unit: 'ppm' },
  V: { element: 'V', limit: 1, unit: 'ppm' },
  W: { element: 'W', limit: 2, unit: 'ppm' },
  Y: { element: 'Y', limit: 1, unit: 'ppm' },
  Zn: { element: 'Zn', limit: 2, unit: 'ppm', limitInPercent: 0.01 },
  Zr: { element: 'Zr', limit: 1, unit: 'ppm' },
};

// Rare earths (ppm)
const RARE_EARTHS: Record<string, MethodElementLimit> = {
  La: { element: 'La', limit: 1, unit: 'ppm' },
  Ce: { element: 'Ce', limit: 1, unit: 'ppm' },
  Pr: { element: 'Pr', limit: 5, unit: 'ppm' },
  Nd: { element: 'Nd', limit: 5, unit: 'ppm' },
  Sm: { element: 'Sm', limit: 5, unit: 'ppm' },
  Eu: { element: 'Eu', limit: 5, unit: 'ppm' },
  Gd: { element: 'Gd', limit: 5, unit: 'ppm' },
  Tb: { element: 'Tb', limit: 5, unit: 'ppm' },
  Dy: { element: 'Dy', limit: 5, unit: 'ppm' },
  Ho: { element: 'Ho', limit: 5, unit: 'ppm' },
  Er: { element: 'Er', limit: 5, unit: 'ppm' },
  Tm: { element: 'Tm', limit: 5, unit: 'ppm' },
  Yb: { element: 'Yb', limit: 5, unit: 'ppm' },
  Lu: { element: 'Lu', limit: 5, unit: 'ppm' },
  Sc: { element: 'Sc', limit: 5, unit: 'ppm' },
  U: { element: 'U', limit: 5, unit: 'ppm' },
};

// Fusion limits (subset from provided table)
const ICPFUSION: Record<string, MethodElementLimit> = {
  Al: { element: 'Al', limit: 0.01, unit: '%' },
  Ca: { element: 'Ca', limit: 0.01, unit: '%' },
  Fe: { element: 'Fe', limit: 0.01, unit: '%' },
  K: { element: 'K', limit: 0.01, unit: '%' },
  Mg: { element: 'Mg', limit: 0.01, unit: '%' },
  Na: { element: 'Na', limit: 0.01, unit: '%' },
  P: { element: 'P', limit: 0.01, unit: '%' },
  Si: { element: 'Si', limit: 0.01, unit: '%' },
  Ti: { element: 'Ti', limit: 0.01, unit: '%' },
  As: { element: 'As', limit: 8, unit: 'ppm' },
  Ba: { element: 'Ba', limit: 2, unit: 'ppm' },
  Be: { element: 'Be', limit: 1, unit: 'ppm' },
  Bi: { element: 'Bi', limit: 10, unit: 'ppm' },
  Cd: { element: 'Cd', limit: 1, unit: 'ppm' },
  Co: { element: 'Co', limit: 5, unit: 'ppm' },
  Cr: { element: 'Cr', limit: 5, unit: 'ppm' },
  Cu: { element: 'Cu', limit: 2, unit: 'ppm' },
  Li: { element: 'Li', limit: 20, unit: 'ppm' },
  Mo: { element: 'Mo', limit: 10, unit: 'ppm' },
  Ni: { element: 'Ni', limit: 20, unit: 'ppm' },
  Pb: { element: 'Pb', limit: 26, unit: 'ppm' },
  Sb: { element: 'Sb', limit: 1, unit: 'ppm' },
  Se: { element: 'Se', limit: 30, unit: 'ppm' },
  Sn: { element: 'Sn', limit: 20, unit: 'ppm' },
  Sr: { element: 'Sr', limit: 2, unit: 'ppm' },
  Ta: { element: 'Ta', limit: 1, unit: 'ppm' },
  Tl: { element: 'Tl', limit: 1, unit: 'ppm' },
  V: { element: 'V', limit: 1, unit: 'ppm' },
  Y: { element: 'Y', limit: 1, unit: 'ppm' },
  Zn: { element: 'Zn', limit: 2, unit: 'ppm' },
};

// 4-acides limits — Stream/Sol variant (PRE.MO/ANA/037)
// Per spec image: Ag in ppb, Cu = 2 ppm (vs Roche's Ag in ppm, Cu = 5 ppm)
const ICP4A_STREAM: Record<string, MethodElementLimit> = {
  ...ICP4A,
  Ag: { element: 'Ag', limit: 5, unit: 'ppb' },
  Cu: { element: 'Cu', limit: 2, unit: 'ppm', limitInPercent: 0.01 },
};

// 4-acides limits — Roche variant (PRE.MO/ANA/038)
// Per spec image: Ag in ppm with limit 1, Cu = 5 ppm
const ICP4A_ROCHE: Record<string, MethodElementLimit> = {
  ...ICP4A,
  Ag: { element: 'Ag', limit: 1, unit: 'ppm' },
  Cu: { element: 'Cu', limit: 5, unit: 'ppm', limitInPercent: 0.01 },
};

// Fusion adds SiO2 / Si as the only major-oxide delta vs 4-acides
const FUSION_SIO2_ONLY: Record<string, MethodElementLimit> = {
  SiO2: { element: 'SiO2', limit: 0.01, unit: '%' },
  Si: { element: 'Si', limit: 0.01, unit: '%' },
};

// ICP Majeurs (PRE.MO/ANA/039) — 10 oxide majors
const ICP_MAJEURS: Record<string, MethodElementLimit> = {
  Al2O3: { element: 'Al2O3', limit: 0.01, unit: '%' },
  CaO: { element: 'CaO', limit: 0.01, unit: '%' },
  Fe2O3: { element: 'Fe2O3', limit: 0.01, unit: '%' },
  K2O: { element: 'K2O', limit: 0.01, unit: '%' },
  MgO: { element: 'MgO', limit: 0.01, unit: '%' },
  MnO: { element: 'MnO', limit: 0.01, unit: '%' },
  P2O5: { element: 'P2O5', limit: 0.01, unit: '%' },
  S: { element: 'S', limit: 0.01, unit: '%' },
  SiO2: { element: 'SiO2', limit: 0.01, unit: '%' },
  TiO2: { element: 'TiO2', limit: 0.01, unit: '%' },
  Al: { element: 'Al', limit: 0.01, unit: '%' },
  Ca: { element: 'Ca', limit: 0.01, unit: '%' },
  Fe: { element: 'Fe', limit: 0.01, unit: '%' },
  K: { element: 'K', limit: 0.01, unit: '%' },
  Mg: { element: 'Mg', limit: 0.01, unit: '%' },
  Mn: { element: 'Mn', limit: 0.01, unit: '%' },
  P: { element: 'P', limit: 0.01, unit: '%' },
  Si: { element: 'Si', limit: 0.01, unit: '%' },
  Ti: { element: 'Ti', limit: 0.01, unit: '%' },
};

// ICP-EAUX (PRE.MO/ANA/058) — 35 elements in mg/l (water analysis)
const ICP_EAUX: Record<string, MethodElementLimit> = {
  Ag: { element: 'Ag', limit: 0.01, unit: 'mg/l' },
  Al: { element: 'Al', limit: 0.01, unit: 'mg/l' },
  As: { element: 'As', limit: 0.01, unit: 'mg/l' },
  B:  { element: 'B',  limit: 0.01, unit: 'mg/l' },
  Ba: { element: 'Ba', limit: 0.01, unit: 'mg/l' },
  Be: { element: 'Be', limit: 0.01, unit: 'mg/l' },
  Bi: { element: 'Bi', limit: 0.01, unit: 'mg/l' },
  Ca: { element: 'Ca', limit: 0.01, unit: 'mg/l' },
  Cd: { element: 'Cd', limit: 0.01, unit: 'mg/l' },
  Co: { element: 'Co', limit: 0.01, unit: 'mg/l' },
  Cr: { element: 'Cr', limit: 0.01, unit: 'mg/l' },
  Cu: { element: 'Cu', limit: 0.01, unit: 'mg/l' },
  Fe: { element: 'Fe', limit: 0.01, unit: 'mg/l' },
  K:  { element: 'K',  limit: 0.01, unit: 'mg/l' },
  Li: { element: 'Li', limit: 0.01, unit: 'mg/l' },
  Mg: { element: 'Mg', limit: 0.01, unit: 'mg/l' },
  Mn: { element: 'Mn', limit: 0.01, unit: 'mg/l' },
  Mo: { element: 'Mo', limit: 0.01, unit: 'mg/l' },
  Na: { element: 'Na', limit: 0.01, unit: 'mg/l' },
  Ni: { element: 'Ni', limit: 0.01, unit: 'mg/l' },
  P:  { element: 'P',  limit: 0.01, unit: 'mg/l' },
  Pb: { element: 'Pb', limit: 0.01, unit: 'mg/l' },
  S:  { element: 'S',  limit: 0.01, unit: 'mg/l' },
  Sb: { element: 'Sb', limit: 0.01, unit: 'mg/l' },
  Se: { element: 'Se', limit: 0.01, unit: 'mg/l' },
  Si: { element: 'Si', limit: 0.01, unit: 'mg/l' },
  Sn: { element: 'Sn', limit: 0.01, unit: 'mg/l' },
  Sr: { element: 'Sr', limit: 0.01, unit: 'mg/l' },
  Ta: { element: 'Ta', limit: 0.01, unit: 'mg/l' },
  Ti: { element: 'Ti', limit: 0.01, unit: 'mg/l' },
  Tl: { element: 'Tl', limit: 0.01, unit: 'mg/l' },
  V:  { element: 'V',  limit: 0.01, unit: 'mg/l' },
  W:  { element: 'W',  limit: 0.01, unit: 'mg/l' },
  Zn: { element: 'Zn', limit: 0.01, unit: 'mg/l' },
  Zr: { element: 'Zr', limit: 0.01, unit: 'mg/l' },
};

// ============================================
// ACTIVE METHOD CATALOG (2026 redesign) — 3 analysis methods run together
// as one standard package, plus conditional SAA method MO/ANA/002.
// ============================================
const MO_ANA_072: Record<string, MethodElementLimit> = { // Eau Régale + ICP
  Ag: { element: 'Ag', limit: 1, unit: 'g/t' },
  Cu: { element: 'Cu', limit: 0.01, unit: '%' },
  Pb: { element: 'Pb', limit: 0.01, unit: '%' },
  Zn: { element: 'Zn', limit: 0.01, unit: '%' },
  As: { element: 'As', limit: 10, unit: 'g/t' },
  Bi: { element: 'Bi', limit: 10, unit: 'g/t' },
  Sb: { element: 'Sb', limit: 10, unit: 'g/t' },
};
const MO_ANA_073: Record<string, MethodElementLimit> = { // H2SO4 + ICP (CuOx)
  CuOx: { element: 'CuOx', limit: 0.1, unit: '%' },
};
const MO_ANA_002: Record<string, MethodElementLimit> = { // SAA conditional (Cu/CuOx >= 1%)
  Cu: { element: 'Cu', limit: 0.01, unit: '%' },
  CuOx: { element: 'CuOx', limit: 0.1, unit: '%' },
};

export const METHOD_LIMITS: Record<MethodCode, Record<string, MethodElementLimit>> = {
  // 2026 redesign — active catalog
  'MO/ANA/072': MO_ANA_072,
  'MO/ANA/073': MO_ANA_073,
  'MO/ANA/002': MO_ANA_002,

  // Legacy long-named codes (kept for backward compatibility with historical samples on prod)
  'D4A-ICP- PRE.MO/ANA/038': { ...ICP4A, ...RARE_EARTHS }, // 41 or 55 with rares
  'FP-ICP PRE.MO/ANA/007': ICPFUSION,

  // Clean codes per spec images (new — these are what new samples will use)
  'PRE.MO/ANA/007': ICPFUSION,                     // ICP Fusion (32 elements)
  'PRE.MO/ANA/037': ICP4A_STREAM,                  // Stream/Sol 4-acides (40 elements, Ag in ppb)
  'PRE.MO/ANA/038': ICP4A_ROCHE,                   // Roche 4-acides (40 elements, Ag/Cu different from /037)
  'PRE.MO/ANA/039': ICP_MAJEURS,                   // ICP Majeurs (10 elements)
  'PRE.MO/ANA/040': RARE_EARTHS,                   // Terre rare (15 elements)
  'PRE.MO/ANA/041': { ...ICP4A_STREAM, ...RARE_EARTHS }, // Stream/Sol + Terres Rares (55 elements)
  'PRE.MO/ANA/058': ICP_EAUX,                      // ICP-EAUX water analysis (35 elements, mg/l)

  // Combined methods — 4 acides + Fusion (per user spec: only adds SiO2)
  // NOT for BGM/ZMSM — those use BGM-MULTI-LAB which is untouched below.
  'PRE.MO/ANA/038+007': { ...ICP4A_ROCHE, ...FUSION_SIO2_ONLY },  // Roche + Fusion
  'PRE.MO/ANA/037+007': { ...ICP4A_STREAM, ...FUSION_SIO2_ONLY }, // Stream/Sol + Fusion

  // BGM — UNCHANGED. BGM samples bypass everything above via useBGMLimits gate
  // on company_name === 'BGM' || 'ZMSM' in supervisors/certificates.
  'BGM-MULTI-LAB': {
    Ag: { element: 'Ag', limit: 1, unit: 'g/t' },
    As: { element: 'As', limit: 1, unit: 'ppm' },
    Mo: { element: 'Mo', limit: 1, unit: 'ppm' },
    Sn: { element: 'Sn', limit: 1, unit: 'ppm' },
    Cu: { element: 'Cu', limit: 0.01, unit: '%' },
    Fe: { element: 'Fe', limit: 0.01, unit: '%' },
    Pb: { element: 'Pb', limit: 0.01, unit: '%' },
    Zn: { element: 'Zn', limit: 0.01, unit: '%' },
    Au: { element: 'Au', limit: 0.05, unit: 'g/t' },
  },
};

export function getMethodDetectionLimit(methodCode: MethodCode | undefined, element: string): MethodElementLimit | undefined {
  if (!methodCode) return undefined;
  const table = METHOD_LIMITS[methodCode];
  if (!table) return undefined;
  return table[element] || table[element?.trim()] || table[element?.toUpperCase()];
}

/**
 * Convert internal ICP storage to display value for method unit.
 * Majors in % are stored as ppm×10000 in `concentration_ppm` (e.g. 97400 → 9.74 %).
 * Legacy rows sometimes store the literal percent in the same field when the value is below 100.
 */
export function icpStoredPpmToDisplayValue(limit: MethodElementLimit | undefined, value: number): number {
  if (!limit || !Number.isFinite(value)) return value;
  if (limit.unit === '%') {
    // Every %-reported analyte (fusion majors AND the 2026 Cu/Pb/Zn/CuOx package)
    // is stored internally as ppm = percent × 10000 by the CSV import, the analyst
    // grid and supervisor edits alike. So convert straight back to % by dividing
    // by 10000. (The previous `value > 100 ? value/10000 : value` heuristic
    // mis-displayed low-but-real grades, e.g. 0.01 % Cu = 100 ppm rendered as
    // "100 %".)
    return value / 10000;
  }
  return value;
}

export function formatWithMethodDetectionLimitFrontend(methodCode: MethodCode | undefined, element: string, value: number): string {
  const limit = getMethodDetectionLimit(methodCode, element);
  if (!limit) {
    return Number.isFinite(value) ? value.toString() : '0';
  }

  // Method reports in % — compare and format in percent space
  if (limit.unit === '%') {
    const pct = icpStoredPpmToDisplayValue(limit, value);
    if (pct <= 0) return `<${limit.limit}`;
    if (pct < limit.limit) return `<${limit.limit}`;
    return pct.toFixed(2);
  }

  if (value <= 0) return `<${limit.limit}`;
  if (value < limit.limit) return `<${limit.limit}`;

  // Golden rule: ppm > 10000 => %
  let displayValue = value;
  let displayUnit: Unit = limit.unit;
  let displayLod = limit.limit;
  if (displayUnit === 'ppm' && value > 10000) {
    displayValue = value / 10000;
    displayUnit = '%';
    displayLod = limit.limitInPercent ?? 0.01;
    if (displayValue < displayLod) return `<${displayLod}`;
  }

  if (displayUnit === '%') return displayValue.toFixed(2);
  if (displayUnit === 'g/t') return displayValue.toFixed(2);
  return displayValue.toFixed(3);
}


